import { VStack } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { withParentSize } from '@visx/responsive';
import {
  WithParentSizeProps,
  WithParentSizeProvidedProps,
} from '@visx/responsive/lib/enhancers/withParentSize';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, withTooltip } from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';
import { max, min } from '@visx/vendor/d3-array';
import { CSSProperties, MouseEvent, ReactNode, TouchEvent, useCallback, useMemo } from 'react';

import { useChartStructureColors } from '../status-tag/chart-colors';

export type DivergingWaterfallDatum = {
  id: string;
  date: Date;
  /** Open risks at the start of the day, before that day's new and closed activity. */
  startingOpenCount: number;
  newCount: number;
  closedCount: number;
  /** Open risks at the end of the day (after new and closed activity). */
  totalOpenCount: number;
};

export type DivergingWaterfallSeries = {
  id: string;
  label: string;
  color: string;
};

export interface DivergingWaterfallChartProps {
  data: DivergingWaterfallDatum[];
  newSeries: DivergingWaterfallSeries;
  closedSeries: DivergingWaterfallSeries;
  totalLineSeries: DivergingWaterfallSeries;
  startingOpenSeries?: DivergingWaterfallSeries;
  dateFormatter?: (date: Date) => string;
  tooltip?: (datum: DivergingWaterfallDatum) => ReactNode;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  bandPaddingInner?: number;
  bandPaddingOuter?: number;
  tooltipStyles?: CSSProperties;
}

const DivergingWaterfallChartResponsive = withParentSize(
  withTooltip(DivergingWaterfallChartComponent),
) as unknown as (
  props: DivergingWaterfallChartProps & Pick<WithParentSizeProps, 'debounceTime'>,
) => JSX.Element;

export const DivergingWaterfallChart = (props: DivergingWaterfallChartProps) => (
  <DivergingWaterfallChartResponsive debounceTime={0} {...props} />
);

function DivergingWaterfallChartComponent({
  data,
  newSeries,
  closedSeries,
  totalLineSeries,
  startingOpenSeries,
  dateFormatter,
  tooltip,
  parentWidth = 0,
  parentHeight = 0,
  margin = {},
  bandPaddingInner = 0.3,
  bandPaddingOuter = 0,
  showTooltip,
  hideTooltip,
  tooltipStyles,
  tooltipData,
  tooltipTop = 0,
  tooltipLeft = 0,
}: DivergingWaterfallChartProps &
  WithTooltipProvidedProps<DivergingWaterfallDatum> &
  WithParentSizeProvidedProps &
  WithParentSizeProps) {
  const {
    top: marginTop = 8,
    right: marginRight = 4,
    bottom: marginBottom = 24,
    left: marginLeft = 24,
  } = margin;
  const width = Math.max(0, parentWidth ?? 0);
  const height = Math.max(0, parentHeight ?? 0);
  const structure = useChartStructureColors();
  const axisColor = structure.axisLabel;
  const axisTickColor = structure.tick;

  const innerWidth = Math.max(0, width - marginLeft - marginRight);
  const innerHeight = Math.max(0, height - marginTop - marginBottom);

  const yMin = useMemo(
    () =>
      min(data, (datum) =>
        Math.min(datum.startingOpenCount - datum.closedCount, datum.totalOpenCount),
      ) ?? 0,
    [data],
  );
  const yMax = useMemo(
    () =>
      max(data, (datum) =>
        Math.max(datum.startingOpenCount + datum.newCount, datum.totalOpenCount),
      ) ?? 0,
    [data],
  );

  // Scope the domain so the full data range fills ~80% of the chart height (10% padding each side).
  const dataRange = Math.max(yMax - yMin, 1);
  const domainPad = dataRange * (10 / 80);
  const domainMin = yMin - domainPad;
  const domainMax = yMax + domainPad;

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: data.map((datum) => datum.id),
        range: [marginLeft, innerWidth + marginLeft],
        paddingInner: bandPaddingInner,
        paddingOuter: bandPaddingOuter,
      }),
    [bandPaddingInner, bandPaddingOuter, data, innerWidth, marginLeft],
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [domainMin, domainMax],
        range: [innerHeight + marginTop, marginTop],
        nice: true,
      }),
    [domainMax, domainMin, innerHeight, marginTop],
  );

  const canRenderChart = innerWidth > 0 && innerHeight > 0 && data.length > 0;

  const yTickFormat = useCallback(
    (value: number | { valueOf(): number }) => String(Math.round(Number(value))),
    [],
  );

  const yTickValues = useMemo(() => {
    const [lo, hi] = yScale.domain();
    if (lo === undefined || hi === undefined) {
      return [];
    }
    const lowerTick = Math.max(0, lo);
    return Math.round(lowerTick) === Math.round(hi) ? [hi] : [lowerTick, hi];
  }, [yScale]);

  const xAxisLabels = useMemo(() => {
    const first = data[0];
    const last = data.length > 1 ? data[data.length - 1] : null;
    const fmt = (d: (typeof data)[0]) => dateFormatter?.(d.date) ?? d.date.toLocaleDateString();
    return { first: first ? fmt(first) : null, last: last ? fmt(last) : null };
  }, [data, dateFormatter]);

  const fullTooltipStyles = useMemo(
    () => ({
      ...defaultStyles,
      background: 'rgba(0, 0, 0, 0.64)',
      borderRadius: '8px',
      color: 'white',
      fontFamily: 'Inter, arial, sans-serif',
      fontSize: '10px',
      fontWeight: 400,
      ...tooltipStyles,
    }),
    [tooltipStyles],
  );

  const handleTooltip = useCallback(
    (event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>) => {
      if (!canRenderChart) {
        return;
      }

      const point = localPoint(event);
      if (!point) {
        return;
      }

      const nearestDatum = data.reduce<{ datum: DivergingWaterfallDatum; distance: number } | null>(
        (closest, datum) => {
          const barX = xScale(datum.id);
          if (barX == null) {
            return closest;
          }

          const barWidth = xScale.bandwidth();
          const distance =
            point.x < barX
              ? barX - point.x
              : point.x > barX + barWidth
                ? point.x - (barX + barWidth)
                : 0;

          if (!closest || distance < closest.distance) {
            return { datum, distance };
          }

          return closest;
        },
        null,
      );

      if (!nearestDatum) {
        return;
      }

      const barX = xScale(nearestDatum.datum.id) ?? marginLeft;
      showTooltip({
        tooltipData: nearestDatum.datum,
        tooltipLeft: barX + xScale.bandwidth() / 2,
        tooltipTop: yScale(nearestDatum.datum.totalOpenCount),
      });
    },
    [canRenderChart, data, marginLeft, showTooltip, xScale, yScale],
  );

  const defaultTooltip = useCallback(
    (datum: DivergingWaterfallDatum) => (
      <VStack alignItems="start" spacing={1} minW="120px">
        <Text size="inherit" weight="bold">
          {dateFormatter?.(datum.date) ?? datum.date.toLocaleDateString()}
        </Text>
        {startingOpenSeries ? (
          <Text size="inherit">
            {startingOpenSeries.label}: {datum.startingOpenCount}
          </Text>
        ) : null}
        <Text size="inherit">
          {newSeries.label}: {datum.newCount}
        </Text>
        <Text size="inherit">
          {closedSeries.label}: {datum.closedCount}
        </Text>
        <Text size="inherit">
          {totalLineSeries.label}: {datum.totalOpenCount}
        </Text>
      </VStack>
    ),
    [closedSeries.label, dateFormatter, newSeries.label, startingOpenSeries, totalLineSeries.label],
  );

  const resolvedTooltipData = tooltipData && Number.isFinite(tooltipLeft) ? tooltipData : undefined;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg width={width} height={height}>
        {canRenderChart ? (
          <>
            {(() => {
              const [domainLo, domainHi] = yScale.domain();
              if (domainLo === undefined || domainHi === undefined) {
                return null;
              }
              if (0 < domainLo || 0 > domainHi) {
                return null;
              }
              const zeroY = yScale(0);
              return (
                <line
                  x1={marginLeft}
                  y1={zeroY}
                  x2={marginLeft + innerWidth}
                  y2={zeroY}
                  stroke={axisTickColor}
                  strokeWidth={1}
                  strokeDasharray="3 2"
                />
              );
            })()}
            <Group>
              {data.map((datum, index) => {
                const nextDatum = data[index + 1];
                if (!nextDatum) return null;
                const barX = xScale(datum.id);
                const nextBarX = xScale(nextDatum.id);
                if (barX == null || nextBarX == null) return null;
                const connectorY = yScale(datum.totalOpenCount);
                return (
                  <line
                    key={`connector-${datum.id}`}
                    x1={barX + xScale.bandwidth()}
                    y1={connectorY}
                    x2={nextBarX}
                    y2={connectorY}
                    stroke={axisTickColor}
                    strokeWidth={1}
                    strokeDasharray="3 2"
                  />
                );
              })}
            </Group>
            <Group>
              {data.map((datum) => {
                const barX = xScale(datum.id);
                if (barX == null) {
                  return null;
                }

                const barWidth = xScale.bandwidth();
                const baselineY = yScale(datum.startingOpenCount);
                const newTopY = yScale(datum.startingOpenCount + datum.newCount);
                const closedBottomY = yScale(datum.startingOpenCount - datum.closedCount);
                const newHeight = baselineY - newTopY;
                const closedHeight = closedBottomY - baselineY;

                return (
                  <Group key={datum.id}>
                    {datum.newCount > 0 ? (
                      <Bar
                        x={barX}
                        y={newTopY}
                        width={barWidth}
                        height={Math.max(newHeight, 0)}
                        fill={newSeries.color}
                        rx={2}
                      />
                    ) : null}
                    {datum.closedCount > 0 ? (
                      <Bar
                        x={barX}
                        y={baselineY}
                        width={barWidth}
                        height={Math.max(closedHeight, 0)}
                        fill={closedSeries.color}
                        rx={2}
                      />
                    ) : null}
                  </Group>
                );
              })}
            </Group>
            <LinePath<DivergingWaterfallDatum>
              data={data}
              x={(datum) => (xScale(datum.id) ?? marginLeft) + xScale.bandwidth() / 2}
              y={(datum) => yScale(datum.totalOpenCount)}
              stroke={totalLineSeries.color}
              strokeWidth={2}
            />
            <AxisLeft
              scale={yScale}
              left={marginLeft}
              tickValues={yTickValues}
              tickFormat={yTickFormat}
              tickLabelProps={{
                fill: axisColor,
                fontSize: 10,
                fontFamily: 'Inter, arial, sans-serif',
                textAnchor: 'end',
                dy: '0.33em',
                dx: -4,
              }}
              hideAxisLine
              hideTicks
            />
            {xAxisLabels.first ? (
              <text
                x={marginLeft}
                y={innerHeight + marginTop + 16}
                textAnchor="start"
                fontSize={10}
                fontFamily="Inter, arial, sans-serif"
                fill={axisColor}
              >
                {xAxisLabels.first}
              </text>
            ) : null}
            {xAxisLabels.last ? (
              <text
                x={marginLeft + innerWidth}
                y={innerHeight + marginTop + 16}
                textAnchor="end"
                fontSize={10}
                fontFamily="Inter, arial, sans-serif"
                fill={axisColor}
              >
                {xAxisLabels.last}
              </text>
            ) : null}
            <Bar
              x={marginLeft}
              y={marginTop}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onTouchStart={handleTooltip}
              onTouchMove={handleTooltip}
              onMouseMove={handleTooltip}
              onMouseLeave={() => hideTooltip()}
            />
          </>
        ) : null}
      </svg>
      {resolvedTooltipData ? (
        <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={fullTooltipStyles}>
          {tooltip?.(resolvedTooltipData) ?? defaultTooltip(resolvedTooltipData)}
        </TooltipWithBounds>
      ) : null}
    </div>
  );
}
